import { motion } from "framer-motion";
import {
  Home,
  Landmark,
  Building2,
  Handshake,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";

const EASE = [0.83, 0, 0.17, 1];

const services = [
  {
    n: "01",
    icon: Home,
    title: "Buyer's Agent Advocacy",
    desc: "Helping investors identify and acquire property opportunities aligned with their long-term goals and risk profile.",
  },
  {
    n: "02",
    icon: Landmark,
    title: "Land Acquisition",
    desc: "Connecting landowners, developers and investors to sites with potential for future disability housing developments.",
  },
  {
    n: "03",
    icon: Building2,
    title: "SDA Investment Opportunities",
    desc: "Supporting investors exploring Specialist Disability Accommodation and introducing them to the right professionals.",
  },
  {
    n: "04",
    icon: Handshake,
    title: "Strategic Partnerships",
    desc: "Connecting investors, developers, providers, support coordinators and housing professionals into meaningful collaborations.",
  },
  {
    n: "05",
    icon: Sparkles,
    title: "Disability Housing Insight",
    desc: "Bringing extensive sector experience together with a practical understanding of housing to inform premium property decisions.",
  },
];

function ServiceCard({ item, index }) {
  const Icon = item.icon;
  return (
    <motion.article
      data-testid={`service-card-${item.n}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: EASE, delay: index * 0.08 }}
      className="group relative p-8 md:p-10 bg-charcoal border border-gold/15 hover:border-gold/60 transition-colors duration-700"
    >
      <div className="absolute top-6 right-6 text-gold/40 group-hover:text-gold text-[11px] tracking-[0.3em] transition-colors duration-500">
        {item.n}
      </div>

      <div className="w-12 h-12 flex items-center justify-center border border-gold/30 group-hover:border-gold group-hover:bg-gold/10 transition-all duration-500 mb-8">
        <Icon size={20} className="text-gold" strokeWidth={1.25} />
      </div>

      <h3 className="font-serif text-3xl md:text-[34px] text-cream leading-tight">
        {item.title}
      </h3>

      <p className="mt-5 text-cream/65 text-sm md:text-[15px] leading-relaxed max-w-md">
        {item.desc}
      </p>

      <div className="mt-10 flex items-center gap-3 text-gold text-[11px] tracking-[0.32em] uppercase opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all duration-500">
        <span className="w-6 h-px bg-gold" />
        Explore
        <ArrowUpRight size={14} />
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-plum py-28 md:py-40 border-t border-gold/10"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE }}
            className="md:col-span-6"
          >
            <div className="flex items-center gap-3 text-gold text-[11px] tracking-[0.35em] uppercase">
              <span className="w-8 h-px bg-gold" />
              How I Can Help
            </div>
            <h2 className="mt-6 font-serif font-light text-cream text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
              A curated{" "}
              <span className="italic text-gold">practice</span>,
              <br />
              built on introductions.
            </h2>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE, delay: 0.15 }}
            className="md:col-span-5 md:col-start-8 text-cream/65 text-base md:text-lg leading-relaxed"
          >
            Five focused offerings, designed to bridge investment, land and the
            disability housing sector with precision, discretion and long-term
            thinking.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((s, i) => (
            <ServiceCard key={s.n} item={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
